import formatDateToYYYYMMDD from '../../../utils/formatDateToYYYYMMDD';
import { getEventLabel } from '../../../constants/eventTypes';
import { getVisibleMonth } from './pickerHelpers';

/**
 * Maps events to a date-to-event-type lookup object
 * Normalizes dates to YYYY-MM-DD format for consistent comparison
 * 
 * @param {Array} events - Array of event objects with date and type properties
 * @returns {Object} Map of normalized dates (YYYY-MM-DD) to event types
 */
export const mapEventsByDate = (events = []) => {
  if (!events || events.length === 0) return {};

  const eventsByDate = {};
  events.forEach((event) => {
    if (event.date && event.type) {
      // Normalize date to YYYY-MM-DD format for comparison
      const normalizedDate = formatDateToYYYYMMDD(event.date);
      if (normalizedDate) {
        // Only assign if not already set - this ensures we show the FIRST event found
        // when multiple events occur on the same day
        if (!eventsByDate[normalizedDate]) {
        eventsByDate[normalizedDate] = event.type;
        }
      }
    }
  });

  return eventsByDate;
};


/**
 * Maps events to unique event types and their labels
 * Used to generate badges for the calendar footer
 * 
 * @param {Array} events - Array of event objects with type property
 * @param {Object} dictionary - Translation dictionary (from preact-i18n, optional)
 * @returns {Array} Array of objects with { type, label } for each unique event type
 */
export const mapEventsToBadges = (events = [], dictionary = null) => {
  if (!events || events.length === 0) return [];

  // Extract unique event types
  const uniqueTypes = new Set();
  events.forEach((event) => {
    if (event.type) {
      uniqueTypes.add(event.type);
    }
  });

  // Map each type to its label (translated if dictionary provided)
  return Array.from(uniqueTypes)
    .map((type) => ({
      type,
      label: getEventLabel(type, dictionary),
    }))
    .filter((badge) => badge.label); // Only include types that have labels
};

/**
 * Filter events to only include those in the currently visible month
 * @param {Array} events - Array of event objects with date property
 * @param {jQuery} $input - jQuery input element (used if visibleMonth not provided)
 * @param {Date} [visibleMonth] - Optional Date object representing visible month (if provided, $input is ignored)
 * @returns {Array} Filtered array of events that belong to the visible month
 */
export const filterEventsByVisibleMonth = (events, $input, visibleMonth = null) => {
  if (!events || events.length === 0) {
    return [];
  }

  // Use visibleMonth if provided directly, otherwise get it from $input
  const monthToUse = visibleMonth || ($input ? getVisibleMonth($input) : null);

  if (!monthToUse) {
    // If can't determine visible month, return all events
    return events;
  }

  const visibleYear = monthToUse.getFullYear();
  const visibleMonthIndex = monthToUse.getMonth();

  return events.filter((event) => {
    if (!event.date) {
      return false;
    }

    // Parse event date (could be string YYYY-MM-DD or Date object)
    const eventDate = typeof event.date === 'string' 
      ? new Date(`${event.date}T00:00:00`) 
      : new Date(event.date);

    if (isNaN(eventDate.getTime())) {
      return false;
    }

    // Check if event is in the visible month
    return eventDate.getFullYear() === visibleYear && 
           eventDate.getMonth() === visibleMonthIndex;
  });
};

